import { Fragment, useEffect, useRef, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { useLocation, useHistory } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";
import { hoursToMilliseconds, secondsToMilliseconds } from "date-fns";
import { useDispatch, useSelector, useSocket } from "../hooks";

import {
  setUserInfo,
  setRoleChoices,
  selectRole,
} from "../store/queue/actions";

import selectDefaultRole from "../utilities/selectDefaultRole";
import * as R from "ramda";
import { ADMIN_ROLE_GROUP_NAME } from "../config";

export default function AuthHandler(props) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { pathname } = useLocation();
  const socketService = useSocket();
  const { authState, oktaAuth } = useOktaAuth();
  const hostedNetworks = useSelector(
    (state: any) => state.queue.hostedNetworks
  );

  const isAuthenticated = authState?.isAuthenticated;
  const loginCompletedRef = useRef(false);
  const socketInitRef = useRef(false);

  const handleLogout = () => {
    //console.log("AuthHandler: Logging out in 10 seconds.");
    setTimeout(async () => {
      //console.log(`AuthHandler: Logging out as of ${new Date().toString()}`);
      await oktaAuth.signOut({ postLogoutRedirectUri: window.location.origin });
    }, secondsToMilliseconds(10));
  };

  const tokenManagerEvents = {
    added: (key, token) => {
      /*console.log(
        `AuthHandler: Token ${key} added at ${new Date().toString()}.`
      );*/
    },
    expired: async (key, token) => {
      /*console.log(
        `AuthHandler: Token ${key} expired at ${new Date().toString()}.`
      );*/
    },
    error: async (err) => {
      console.error(
        `AuthHandler: Token manager experienced critical error at ${new Date().toString()}:`
      );
      console.dir(err);
      console.error("THIS WILL TRIGGER A LOGOUT EVENT");
      handleLogout();
    },
    renewed: (key, newToken, oldToken) => {
      /*console.log(
        `AuthHandler: Token ${key} was renewed at ${new Date().toString()}.`
      );*/
    },
    removed: (key, token) => {
      /*console.log(
        `AuthHandler: Token ${key} was removed at ${new Date().toString()}.`
      );*/
    },
  };

  const addTokenListeners = () => {
    Object.keys(tokenManagerEvents).forEach((tokenEventName: any) => {
      oktaAuth.tokenManager.on(
        tokenEventName,
        tokenManagerEvents[tokenEventName]
      );
      /*console.log(
        `AuthHandler: Okta token event listener "${tokenEventName}" instantiated.`
      );*/
    });
  };

  const removeTokenListeners = () => {
    Object.keys(tokenManagerEvents).forEach((tokenEventName: any) => {
      oktaAuth.tokenManager.off(tokenEventName);
      /*console.log(
        `AuthHandler: Okta token event listener "${tokenEventName}" removed (if applicable).`
      );*/
    });
  };

  const onActive = () => {
    //console.warn("AuthHandler: onActive was called. It should not have been.");
  };

  const onIdle = async () => {
    /*console.log(
      `AuthHandler: Detected that user is idle as of ${new Date().toString()}.`
    );*/
    handleLogout();
  };

  useIdleTimer({
    debounce: 0,
    events: ["keydown", "mousedown", "MSPointerDown"],
    timeout: hoursToMilliseconds(2),
    onActive,
    onIdle,
  });

  useEffect(() => {
    window.sessionStorage.removeItem("okta-auth-js");
    // optional: also clear localStorage if you use it
    window.localStorage.removeItem("okta-auth-js");

    const getUserInfo = async () => {
      const userInfo = oktaAuth.getUser();
      return userInfo || null;
    };

    const hasHostedNetworks =
      hostedNetworks &&
      Array.isArray(hostedNetworks) &&
      hostedNetworks.length > 0;

    if (isAuthenticated && !hasHostedNetworks) {
      if (!socketInitRef.current) {
        /*console.warn(
          "AuthHandler: No hosted networks found; initiating socket connection."
        );*/
        addTokenListeners();
        socketService.init();
        socketInitRef.current = true;
      } /* else {
        console.warn(
          "AuthHandler: Waiting for socket initiation to be completed."
        );
      }*/
    } else {
      if (!loginCompletedRef.current && hasHostedNetworks) {
        const networkName = hostedNetworks[0];
        /*console.log(
          `AuthHandler: Hosted network name retrieved: "${networkName}"; Getting user info.`
        );*/
        getUserInfo().then((userInfo) => {
          if (userInfo) {
            /*console.log(
              "AuthHandler: User info retrieved, continuing sign-in."
            );*/

            const parsedUserInfo = {
              ...userInfo,
              displayName:
                userInfo.displayName ??
                userInfo.name ??
                `${userInfo.given_name} ${userInfo.family_name}`,
              networks: [networkName],
            };
            const roleChoices = parseRolesForUser(userInfo);

            dispatch(setUserInfo(parsedUserInfo));
            dispatch(setRoleChoices(roleChoices));

            const defaultRole = selectDefaultRole(roleChoices);
            dispatch(selectRole(defaultRole));
            loginCompletedRef.current = true;
          } else {
            console.error(
              "AuthHandler: Critical error occurred during login: No User info has been retrieved."
            );
            loginCompletedRef.current = true;
          }
        });
      }
    }

    return () => {
      removeTokenListeners();
    };
  }, [hostedNetworks, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && pathname.startsWith("/login")) {
      history.push("/");
    }
  }, [history, isAuthenticated, pathname]);

  return <Fragment>{props.children}</Fragment>;
}

const desiredRoleChoices = [
  "Role_CallProducer",
  "Role_CallScreener",
  "Role_CallScheduler",
  "Role_CallBooker",
  "Role_HelpDesk",
  "Role_QueueAdmin",
  "Role_QueueTech",
  "Role_StageDoor",
  "Role_MultiboxController",
  "Role_AudienceQueue",
  "Role_PowerUser",
];

function parseRoleName(_roleName) {
  let t = _roleName.replace("Role_", "");
  t = t.charAt(0).toLowerCase() + t.substr(1);
  return t;
}

function parseRole(roleName: string, userGroups: string[]) {
  const roleAlias = R.cond([
    [R.equals("Role_CallScheduler"), R.always("Call Scheduler")],
    [R.equals("Role_CallBooker"), R.always("Call Booker")],
    [R.equals("Role_HelpDesk"), R.always("Help Desk")],
    [R.equals("Role_QueueAdmin"), R.always("Queue Admin")],
    [R.equals("Role_QueueTech"), R.always("Queue Tech")],
    [R.equals("Role_CallScreener"), R.always("Call Screener")],
    [R.equals("Role_StageDoor"), R.always("StageDoor Manager")],
    [R.equals("Role_MultiboxController"), R.always("CrowdView Operator")],
    [R.equals("Role_AudienceQueue"), R.always("Audience Queue Operator")],
    [R.equals("Role_PowerUser"), R.always("Power User")],
    [R.T, R.always("Call Producer")],
  ])(roleName);

  if (userGroups.includes(roleName)) {
    return {
      name: parseRoleName(roleName),
      alias: roleAlias,
      value: true,
    };
  } else {
    return {
      name: parseRoleName(roleName),
      alias: roleAlias,
      value: false,
    };
  }
}

function parseRolesForUser(userInfo) {
  // Shim to ensure basic functionality for users as we transition away from Okta

  //console.log("parseRolesForUser", userInfo);

  let userGroups = [
    "Role_CallScheduler",
    "Role_CallBooker",
    "Role_CallProducer",
    "Role_CallScreener",
    "Role_StageDoor",
  ];

  if (!ADMIN_ROLE_GROUP_NAME) {
    console.error(
      "AuthHandler: An admin role group name was not defined for this environment."
    );
  } else {
    // Check for a boolean property on userInfo matching ADMIN_ROLE_GROUP_NAME
    if (userInfo?.[ADMIN_ROLE_GROUP_NAME] === true) {
      console.warn(
        `AuthHandler: User is a member of the admin group "${ADMIN_ROLE_GROUP_NAME}".`
      );
      userGroups.push("Role_QueueAdmin");
      userGroups.push("Role_QueueTech");
    }
  }
  // TODO: Add "power users" role when needed

  return desiredRoleChoices.map((roleChoice) =>
    parseRole(roleChoice, userGroups)
  );
}
